import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAdminReviews, moderateReview } from '../../api';
import {
  PageHeader, Table, Pagination, EmptyState, TableSkeleton, StatusBadge, UserCell, AstrologerCell,
} from '../../components/ui';
import Modal from '../../components/ui/Modal';
import OverviewBar from '../../components/ui/OverviewBar';
import ExportButton from '../../components/ui/ExportButton';
import { formatDateTime } from '../../utils/format';
import { Star, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';

const LIMIT = 20;

export default function ReviewsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [approvedFilter, setApprovedFilter] = useState<string>('');
  const [selected, setSelected] = useState<any | null>(null);
  const [note, setNote] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-reviews', page, approvedFilter],
    queryFn: () =>
      getAdminReviews({
        page,
        limit: LIMIT,
        isApproved: approvedFilter === '' ? undefined : approvedFilter,
      }).then((r) => r.data.data),
    placeholderData: (prev) => prev,
  });

  const rows = data?.data || [];
  const pagination = data?.pagination;

  const moderate = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' }) =>
      moderateReview(id, { action, adminNote: note || undefined }),
    onSuccess: () => {
      toast.success('Review updated');
      qc.invalidateQueries({ queryKey: ['admin-reviews'] });
      setSelected(null);
      setNote('');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Moderation failed'),
  });

  const EXPORT_COLS = [
    { key: '_id', label: 'ID' },
    { key: 'rating', label: 'Rating' },
    { key: 'comment', label: 'Comment' },
    { key: 'isApproved', label: 'Approved' },
    { key: 'createdAt', label: 'Date' },
  ];

  return (
    <div className="p-6">
      <PageHeader title="Reviews" subtitle="Moderate user reviews (PDF §14)">
        <ExportButton data={rows} filename="reviews" columns={EXPORT_COLS} />
      </PageHeader>

      <OverviewBar
        items={[
          { label: 'Total', value: pagination?.total ?? '—', icon: <Star size={14} /> },
          { label: 'On page', value: rows.length },
        ]}
        loading={isLoading}
      />

      <div className="card p-4 mb-5 flex flex-wrap gap-2">
        {[
          { v: '', l: 'All' },
          { v: 'true', l: 'Approved' },
          { v: 'false', l: 'Pending / rejected' },
        ].map((f) => (
          <button
            key={f.v || 'all'}
            onClick={() => { setApprovedFilter(f.v); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${approvedFilter === f.v ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300'}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-5"><TableSkeleton rows={8} cols={6} /></div>
        ) : rows.length === 0 ? (
          <EmptyState message="No reviews" icon={<Star size={32} />} />
        ) : (
          <Table>
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="table-th">User</th>
                <th className="table-th">Astrologer</th>
                <th className="table-th">Rating</th>
                <th className="table-th">Comment</th>
                <th className="table-th">Status</th>
                <th className="table-th">Date</th>
                <th className="table-th"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any) => (
                <tr key={r._id} className="table-row">
                  <td className="table-td"><UserCell entity={r.userId} /></td>
                  <td className="table-td"><AstrologerCell entity={r.astrologerId} /></td>
                  <td className="table-td font-semibold">{r.rating}★</td>
                  <td className="table-td text-xs max-w-[220px] truncate">{r.comment || r.review || '—'}</td>
                  <td className="table-td">
                    <StatusBadge status={r.isApproved ? 'approved' : 'pending'} />
                  </td>
                  <td className="table-td text-xs">{formatDateTime(r.createdAt)}</td>
                  <td className="table-td">
                    <button className="btn-secondary btn-sm" onClick={() => { setSelected(r); setNote(r.adminNote || ''); }}>
                      Moderate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {pagination?.pages > 1 && (
          <div className="p-4 border-t">
            <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={LIMIT} onPageChange={setPage} />
          </div>
        )}
      </div>

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Moderate review">
        {selected && (
          <div className="space-y-3">
            <p className="text-sm">{selected.comment || selected.review || 'No comment'}</p>
            <p className="text-xs text-gray-500">Rating: {selected.rating}★</p>
            <div>
              <label className="block text-xs font-medium mb-1">Admin note</label>
              <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <button
                className="btn-secondary text-red-600"
                disabled={moderate.isPending}
                onClick={() => moderate.mutate({ id: selected._id, action: 'reject' })}
              >
                <X size={14} /> Reject
              </button>
              <button
                className="btn-primary"
                disabled={moderate.isPending}
                onClick={() => moderate.mutate({ id: selected._id, action: 'approve' })}
              >
                <Check size={14} /> Approve
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
